// ConfirmContext.ts
import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import "../components/Generic/ConfirmDialog/styles.scss";
interface ConfirmOptions {
    message: string;
    onConfirm?: () => void;
    onCancel?: () => void;
}

interface ConfirmContextType {
    confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

interface ConfirmProviderProps {
    children: ReactNode;
}

export const ConfirmProvider = ({ children }: ConfirmProviderProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState<ConfirmOptions | null>(null);
    const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

    const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
        return new Promise(resolve => {
            setOptions(opts);
            setIsOpen(true);
            setResolvePromise(() => resolve);
        });
    }, []);

    const handleConfirm = () => {
        if (options?.onConfirm) {
            options.onConfirm();
        }
        if (resolvePromise) {
            resolvePromise(true);
        }
        setIsOpen(false);
        setOptions(null);
        setResolvePromise(null);
    };

    const handleCancel = () => {
        if (options?.onCancel) {
            options.onCancel();
        }
        if (resolvePromise) {
            resolvePromise(false);
        }
        setIsOpen(false);
        setOptions(null);
        setResolvePromise(null);
    };

    return (
        <ConfirmContext.Provider value={{ confirm }}>
            {children}
            {isOpen && options && (
                <div
                    className="confirm-comp"
                    style={{
                        zIndex: "1545564654564654",
                    }}
                >
                    <div className="content">
                        <section className="body">
                            <p>{options.message}</p>
                        </section>
                        <section className="buttons-section">
                            <div className="buttons-wrapper">
                                {options?.onConfirm && (
                                    <button
                                        onClick={handleConfirm}
                                        className="g-btn g-btn-action"
                                    >
                                        {" "}
                                        Ok{" "}
                                    </button>
                                )}
                                <button
                                    onClick={handleCancel}
                                    className="g-btn g-btn-alternate"
                                >
                                    {" "}
                                    Cancel{" "}
                                </button>
                            </div>
                        </section>
                    </div>
                </div>
            )}
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error("useConfirm must be used within a ConfirmProvider");
    }
    return context;
};
